import { ARENA, BOSS } from '../../config/constants.js';

const HIT_FLASH_DURATION = 0.08; // brief white flinch flash on taking damage
// The Swell (Act II) reads `recentDamage` — a decaying accumulator of damage
// taken — to scale its volleys to the player's recent output. Time-constant of
// the exponential decay, in seconds; ~how far back "recent" reaches. Harmless
// (just decays to ~0) for every other boss, which never reads it.
const RECENT_DAMAGE_WINDOW = 2.5;

/**
 * Per-boss movement (BOSS-REWORK.md §B4).
 *
 * The body is on screen 100% of a fight while any one card is on maybe 15% of
 * it, so nineteen bosses sharing one uniform-random wander was the boss
 * layer's largest identity flattener — larger than any pattern-roster
 * decision. Each strategy answers one question: given the boss, the player and
 * the arena, where does this boss want to stand next?
 *
 * A strategy returns `{ x, y, wait }` — the next wander target and how long to
 * hold it before re-deciding. Everything else (the actual lerp toward the
 * target, the `locked` suspension while a pattern drives position, the
 * phase speed bonus) is unchanged and shared.
 *
 * `wander` is the default and is byte-for-byte the old behaviour, so a def
 * that names no `movement` is untouched. Strategies that need to know where
 * the player is fall back to `wander` when they aren't given one — Boss.update
 * takes the player as an OPTIONAL argument, and several bench/test callers
 * drive a boss without one.
 */
const MARGIN_SCALE = 2; // wander margin, in boss radii — kept from the original wander

function clampToArena(x, y, radius) {
  const margin = radius * MARGIN_SCALE;
  return {
    x: Math.min(Math.max(x, ARENA.x + margin), ARENA.x + ARENA.width - margin),
    y: Math.min(Math.max(y, ARENA.y + margin), ARENA.y + ARENA.height - margin),
  };
}

export const MOVEMENT_STRATEGIES = {
  /** Uniform random drift inside the arena margins. The historical default. */
  wander(boss) {
    const margin = boss.radius * MARGIN_SCALE;
    return {
      x: ARENA.x + margin + Math.random() * (ARENA.width - margin * 2),
      y: ARENA.y + margin + Math.random() * (ARENA.height - margin * 2),
      wait: 1.0 + Math.random() * 1.5,
    };
  },

  /**
   * Keeps its distance: retreats to a ring `preferredRange` away from the
   * player, picking the point on that ring nearest to where it already is, so
   * it backs off rather than teleporting around. Re-decides quickly, because a
   * boss that keeps its distance has to keep answering an approach.
   */
  keepAway(boss, player, { preferredRange = 200 } = {}) {
    const dx = boss.x - player.x;
    const dy = boss.y - player.y;
    const dist = Math.hypot(dx, dy) || 1;
    const target = clampToArena(
      player.x + (dx / dist) * preferredRange,
      player.y + (dy / dist) * preferredRange,
      boss.radius,
    );
    return { ...target, wait: 0.5 + Math.random() * 0.4 };
  },

  /**
   * Closes. Walks most of the way to the player and stops just short of
   * contact, so the pressure is positional rather than a ram — the arena has
   * nowhere to run to, which is the point.
   */
  stalk(boss, player, { standoff = 70 } = {}) {
    const dx = player.x - boss.x;
    const dy = player.y - boss.y;
    const dist = Math.hypot(dx, dy) || 1;
    const reach = Math.max(0, dist - standoff);
    const target = clampToArena(boss.x + (dx / dist) * reach, boss.y + (dy / dist) * reach, boss.radius);
    return { ...target, wait: 0.35 + Math.random() * 0.3 };
  },

  /**
   * Mirrors the player across the arena's centre — wherever the player runs,
   * the boss takes the opposite station. Corners are the trap: a player who
   * hugs one edge pins the boss to the far one and hands it the whole board.
   */
  mirror(boss, player) {
    const cx = ARENA.x + ARENA.width / 2;
    const cy = ARENA.y + ARENA.height / 2;
    const target = clampToArena(cx - (player.x - cx), cy - (player.y - cy), boss.radius);
    return { ...target, wait: 0.4 + Math.random() * 0.3 };
  },

  /**
   * Never moves. A throne sits on its throne — the whole fight is authored
   * around a fixed centre, and drifting off it was always a bug in fiction.
   */
  anchor(boss) {
    return { x: boss.spawnX, y: boss.spawnY, wait: 1.5 };
  },
};

/**
 * A single increasingly-dangerous boss within the gauntlet (see
 * data/bosses.js). Physical size and phase-gating are shared across every
 * boss (BOSS.radius/phaseThresholds); `bossDef` supplies what actually
 * escalates fight-to-fight — HP pool and base move speed.
 */
export class Boss {
  constructor(eventBus, bossDef) {
    this.eventBus = eventBus;
    this.bossDef = bossDef;
    // Generalizes CombatSystem's old `enemy === this.boss` identity checks
    // (minionDamageMultiplier exemption, no knockback, isDying/invulnerable
    // gating) to work for a second simultaneous herald too, which lives in
    // CombatSystem.minions (added via addMinion) rather than the single
    // `boss` slot — see _spawnMinibossFromQueue in Game.js.
    this.isBossTier = true;
    this.x = ARENA.x + ARENA.width / 2;
    this.y = ARENA.y + ARENA.height / 2 - 20;
    // Where `anchor` (§B4) returns to, and where any strategy can read as this
    // boss's home station. Captured before anything moves the entity.
    this.spawnX = this.x;
    this.spawnY = this.y;
    // §B4 — a def naming no movement gets the historical uniform wander, and
    // an unknown name falls back to it too rather than freezing the boss.
    this.movement = MOVEMENT_STRATEGIES[bossDef.movement] ? bossDef.movement : 'wander';
    this.movementConfig = bossDef.movementConfig ?? {};
    this.radius = BOSS.radius;
    this.maxHp = bossDef.maxHp;
    this.hp = bossDef.maxHp;
    this.phase = 0;
    this.moveTimer = 0;
    this.targetX = this.x;
    this.targetY = this.y;

    // Driven by attack patterns rather than read directly by other systems:
    // `locked` suspends idle wander while a pattern is moving the boss
    // itself (e.g. Dash Slam); `invulnerable` is toggled by desperation-
    // phase patterns (e.g. Shield Burst) to force dodge-only play.
    this.locked = false;
    this.invulnerable = false;
    // Card practice (BULLET-HELL.md §D8): the boss cannot be killed, so the
    // chosen spell card can be re-run until it's learned. A separate latch
    // from `invulnerable` on purpose — that one is a temporary state patterns
    // and LevySystem toggle on and off during a fight, and a practice session
    // that ended the first time a levy cleared it would be a bug that only
    // showed up on three of the bosses.
    this.practiceLock = false;
    this.vulnerable = false;
    this.vulnerabilityMultiplier = 1.5;
    this._vulnerabilityTimer = 0;

    // `isDying` is a distinct beat from actually dying: hp hits 0, the boss
    // goes invulnerable, and SpawningSystem swaps in that boss's one-shot
    // bespoke desperation pattern (bossDef.createDesperationPattern) instead
    // of picking from the normal library. Only once that pattern finishes
    // (finishDying()) does 'boss:died' actually fire — see SpawningSystem.
    this.isDying = false;

    // Read by Game.js's _drawBoss to flash the sprite white for a beat on
    // hit — the classic "you just landed a hit" flinch cue that was
    // missing (only the particle burst/shake in _wireJuice existed before).
    this.hitFlashTimer = 0;

    // Decaying tally of damage taken, read by the Swell's mirror (see
    // RECENT_DAMAGE_WINDOW). Rises in takeDamage, decays in update.
    this.recentDamage = 0;

    this._pickNewTarget();
  }

  get hpFraction() {
    return this.hp / this.maxHp;
  }

  get currentPhase() {
    let p = 0;
    for (const thresh of BOSS.phaseThresholds) {
      if (this.hpFraction <= thresh) p++;
      else break;
    }
    return p;
  }

  /**
   * `player` is optional: §B4's player-relative strategies need it, and every
   * caller that drives a boss without one (benches, unit tests) simply gets
   * the uniform wander they had before.
   */
  update(dt, player = null) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);
    // Exponential decay toward 0 (before the `locked` early-return, so it keeps
    // fading even while a pattern is driving the boss's position).
    this.recentDamage = Math.max(0, this.recentDamage - (this.recentDamage * dt) / RECENT_DAMAGE_WINDOW);
    if (this._vulnerabilityTimer > 0) {
      this._vulnerabilityTimer -= dt;
      if (this._vulnerabilityTimer <= 0) {
        this.vulnerable = false;
        // Mirrors boss:vulnerability-open — without it the reward window
        // closing was invisible (the green ring just vanished).
        this.eventBus.emit('boss:vulnerability-close');
      }
    }

    const newPhase = this.currentPhase;
    if (newPhase !== this.phase) {
      this.phase = newPhase;
      // Fires for every boss-tier enemy, heralds included (currentPhase is
      // shared BOSS.phaseThresholds logic, not final-boss-only) — carries a
      // reference for the same reason miniboss:died does, so Game.js's
      // handler doesn't assume it's always this.boss.
      this.eventBus.emit('boss:phase-change', { phase: newPhase, boss: this });
    }

    if (this.locked) return; // an attack pattern (e.g. Dash Slam) is driving position directly

    this.moveTimer -= dt;
    if (this.moveTimer <= 0) this._pickNewTarget(player);

    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 1) {
      const speed = this.bossDef.moveSpeed + this.phase * 8;
      this.x += (dx / dist) * speed * dt;
      this.y += (dy / dist) * speed * dt;
    }
  }

  _pickNewTarget(player = null) {
    // `anchor` is the one strategy that works without a player, so it is
    // allowed through; everything else player-relative degrades to wander.
    const needsPlayer = this.movement !== 'wander' && this.movement !== 'anchor';
    const name = needsPlayer && !player ? 'wander' : this.movement;
    const { x, y, wait } = MOVEMENT_STRATEGIES[name](this, player, this.movementConfig);
    this.targetX = x;
    this.targetY = y;
    this.moveTimer = wait;
  }

  takeDamage(amount) {
    if (this.invulnerable || this.practiceLock) return;
    const hpBefore = this.hp;
    const finalAmount = amount * (this.vulnerable ? this.vulnerabilityMultiplier : 1);
    this.hp = Math.max(0, this.hp - finalAmount);
    // ProgressionSystem's XP is driven off this event's `amount` (see
    // _onBossDamage) — emitting the clamped HP delta rather than the raw
    // incoming hit keeps a killing-blow overkill (a big multi-weapon burst
    // landing on a sliver of remaining HP, most common right as the boss
    // dies into desperation) from paying out XP for damage that never
    // actually applied, which was spiking several level-ups at once right
    // as desperation began.
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.recentDamage += appliedDamage; // feeds the Swell's mirror

    if (this.bossDef.isMiniboss) {
      // Economically a very tough minion, not a gauntlet boss — routed
      // through the same minion:damaged channel every other minion uses
      // (ProgressionSystem's XP + Currency's minionDamageDealt credit)
      // instead of boss:damaged, which ProgressionSystem.totalDamageDealt
      // scales against the real boss's own maxHp (see Currency.calcEchoes)
      // and would be corrupted by a second, unrelated HP pool feeding into
      // it. miniboss:damaged is a separate event for Game.js's own
      // dedicated (smaller-than-a-real-boss) hit juice.
      //
      // Deliberately WITHOUT the `minion` field every real minion now passes
      // (BULLET-HEAVEN.md §H1): that field is what routes damage-XP into a
      // banked mote instead of straight into the bar. A herald is a duel, not
      // a harvest — it should not scatter a hundred motes across a fight the
      // player is meant to be reading telegraphs during. Omitting it keeps
      // heralds on the pre-mote direct-credit path, which is the intent.
      this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y });
      this.eventBus.emit('miniboss:damaged', { amount: appliedDamage, hp: this.hp, maxHp: this.maxHp });
    } else {
      this.eventBus.emit('boss:damaged', { amount: appliedDamage, hp: this.hp, maxHp: this.maxHp });
    }
    if (this.hp <= 0 && !this.isDying) {
      this.isDying = true;
      this.invulnerable = true;
      this.locked = true; // hold position for the desperation pattern rather than idly wandering through it
      // Minibosses never have a desperation pattern (see data/bosses.js's
      // createMiniboss), so SpawningSystem calls finishDying() on the very
      // next tick regardless — but boss:desperation-start still fires
      // instantly here either way, and SurviveOverlay listens for exactly
      // that event to show its countdown. Without this guard, every
      // miniboss kill would flash that overlay for a single meaningless frame.
      if (!this.bossDef.isMiniboss) this.eventBus.emit('boss:desperation-start');
    }
  }

  /** Opens a short, readable reward window after a signature attack resolves. */
  openVulnerability(duration = 0.7, multiplier = 1.5) {
    if (this.isDying) return;
    this.vulnerable = true;
    this.vulnerabilityMultiplier = multiplier;
    this._vulnerabilityTimer = duration;
    this.eventBus.emit('boss:vulnerability-open', { duration });
  }

  /** Called by SpawningSystem once the desperation pattern it swapped in has finished playing. */
  finishDying() {
    if (this.bossDef.isMiniboss) {
      // Same dual-emit reasoning as takeDamage above: minion:died keeps it
      // inside the ordinary minion-kill economy (flat XP bonus, no Shards/
      // Echo-per-boss-kill), miniboss:died is what Game.js's state machine
      // and its own death juice actually key off. No `minion` field, for the
      // same reason takeDamage omits it — a herald pays into the bar directly
      // rather than dropping a mote.
      this.eventBus.emit('minion:died', { x: this.x, y: this.y });
      // Payload carries which instance died — two heralds can now be up at
      // once (see Game.js's _spawnMinibossFromQueue), so the bare event
      // Game.js used to key its single this.boss/this.spawning slot off of
      // is no longer enough to tell them apart.
      this.eventBus.emit('miniboss:died', { boss: this });
    } else {
      this.eventBus.emit('boss:died');
    }
  }
}
